import React, { useEffect, useState } from "react";
import styled from "styled-components";
import "./home.css";
import { motion } from "framer-motion";
import images from "../images/Gower";
import JL from "../images/Jeanne+Leo";
import MC from "../images/Madeline+Caleb";
import DD from "../images/Desteni+Devin";
import LoadingSpinner from "../Components/LoadingSpinner";

const album1 = DD;
const album2 = MC;
const album3 = JL;
const album4 = images;

function AlbumView() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [album, setAlbum] = useState([]);
  const [albumName, setAlbumName] = useState("");
  const [albumName2, setAlbumName2] = useState("");
  const [loaded, setLoaded] = useState(false);

  const albums = [album1, album2, album3, album4];
  const albumNames = [
    { line1: "Desteni + Devin" },
    { line1: "Madeline + Caleb", line2: "Engagement" },
    { line1: "Jeanne + Leo", line2: "Anniversary" },
    { line1: "The Gower Family" },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    const idx = window.location.pathname.split("/")[2] - 1;
    setAlbum(albums[idx]);
    setAlbumName(albumNames[idx].line1);
    setAlbumName2(albumNames[idx].line2 ?? undefined);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const idx = window.location.pathname.split("/")[2] - 1;
    Promise.all(
      albums[idx].map(
        (src) =>
          new Promise((resolve) => {
            const img = new Image();
            img.src = src;
            img.onload = resolve;
          })
      )
    ).then(() => setLoaded(true));
  }, []);

  function handleResize() {
    setIsMobile(window.innerWidth <= 900);
  }

  return (
    <Wrapper>
      <Hero>
        <HeroOverlay />
        <HeroAura />

        <HeroContent>
          <HeroKicker>Featured Album</HeroKicker>
          <HeroTitle>{albumName}</HeroTitle>
          {albumName2 && <HeroSubtitle>{albumName2}</HeroSubtitle>}
          <HeroLine />
          <HeroHint>
            A curated set of frames with soft light, texture, and story-forward
            pacing.
          </HeroHint>
        </HeroContent>
      </Hero>

      <GallerySection>
        <CardGrid isMobile={isMobile}>
          {loaded ? (
            album.map((imgSrc, idx) => (
              <ImageCard
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: idx * 0.03 }}
              >
                <Photo src={imgSrc} decoding="auto" />
              </ImageCard>
            ))
          ) : (
            <LoadingState>
              <LoadingSpinner />
              <LoadLabel>Preparing your album…</LoadLabel>
            </LoadingState>
          )}
        </CardGrid>
      </GallerySection>
    </Wrapper>
  );
}

export default AlbumView;

/* ------------------------------------------------------------- */
/*                              STYLES                            */
/* ------------------------------------------------------------- */

const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background: linear-gradient(180deg, #ada98d 0%, #b7b295 35%, #9c9777 100%);
  color: #1f1c1a;
`;

/* PERFECT, BULLETPROOF HERO SECTION */
const Hero = styled.section`
  width: 100%;
  min-height: 52vh;
  padding: 24px 16px;

  position: relative;
  overflow: hidden;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);

  width: 100%;
  max-width: 900px;
  height: 100%;
  padding: 24px 16px;

  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 28px;
  box-shadow: 0 30px 80px rgba(20, 18, 14, 0.22),
    0 0 0 1px rgba(255, 255, 255, 0.05);

  pointer-events: none;
`;

const HeroAura = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%) rotate(-0deg);

  width: 180%;
  height: 180%;
  opacity: 0.8;

  background: radial-gradient(
    circle at 30% 30%,
    rgba(255, 244, 217, 0.7),
    rgba(173, 169, 141, 0)
  );

  filter: blur(40px);

  @media (max-width: 600px) {
    width: 220%;
    height: 220%;
    filter: blur(32px);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;

  width: 100%;
  max-width: 700px;
  padding: 24px 8px;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  margin: 0 auto;
`;

/* Text Styles */
const HeroKicker = styled.p`
  letter-spacing: 0.24em;
  text-transform: uppercase;
  font-size: 0.95rem;
  color: rgba(246, 243, 234, 0.78);
  margin-bottom: 14px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.4rem, 5vw, 3.4rem);
  margin: 0;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: rgba(246, 243, 234, 0.85);
`;

const HeroSubtitle = styled.h2`
  font-size: clamp(1.4rem, 3vw, 2rem);
  margin-top: 12px;
  color: rgba(246, 243, 234, 0.85);
  font-weight: 400;
`;

const HeroLine = styled.div`
  width: 120px;
  height: 2px;
  background: rgba(246, 243, 234, 0.6);
  margin: 24px auto 16px;
`;

const HeroHint = styled.p`
  max-width: 600px;
  font-size: 1rem;
  color: rgba(246, 243, 234, 0.86);
  line-height: 1.6;
  margin: 0 auto;
  @media (max-width: 600px) {
    width: 90%;
  }
`;

/* Gallery styles */

const GallerySection = styled.section`
  position: relative;
  padding: clamp(48px, 8vw, 96px) clamp(20px, 5vw, 72px) clamp(64px, 8vw, 120px);
  display: flex;
  flex-direction: column;
  gap: 28px;
  margin-top: clamp(-120px, -16vw, -80px);
  z-index: 2;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ isMobile }) =>
    isMobile ? "1fr" : "repeat(auto-fit, minmax(260px, 1fr))"};
  gap: clamp(18px, 2vw, 26px);
  width: 100%;
  margin: 0 auto;
`;

const ImageCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.18);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 18px 30px rgba(25, 23, 19, 0.28),
    inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LoadingState = styled.div`
  display: grid;
  gap: 12px;
  justify-items: center;
  padding: 48px 0;
`;

const LoadLabel = styled.span`
  font-size: 0.95rem;
  color: rgba(31, 28, 26, 0.8);
`;
